import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import { Role, isBranchStaffRole, isB2BRole } from '@/enums/role';
import { useTranslation } from 'react-i18next';
import { Company } from '@/types/Company.interface';
import { Branch } from '@/types/Branch.interface';
import { companyService } from '@/data/companyService';
import { branchService } from '@/data/branchService';
import { authService } from '@/data/authService';
import { useUserViewMode } from '@/hooks/useUserViewMode';
import { user$ } from '@/store/userStore';
import { User } from '@/types/User.interface';
import { UserStatus } from '@/enums/user-status';
import { formatTurkishPhone, getAvailableRoles } from '@/utils/helpers';

type UserDialogProps = {
  open: boolean;
  onClose: () => void;
  error?: string;
  user?: User | null;
  onSave: (user: {
    name: string;
    surname: string;
    email?: string;
    phone: string;
    role: Role;
    status?: UserStatus;
    password?: string;
    companyId?: number;
    branchId?: number;
    otp?: string;
  }) => Promise<void>;
};

const UserDialog = ({
  open,
  onClose,
  onSave,
  error,
  user
}: UserDialogProps) => {
  const [name, setName] = useState('');
  const [surname, setSurname] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState<Role | ''>('');
  const [status, setStatus] = useState<UserStatus | ''>('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | ''>('');
  const [selectedBranchId, setSelectedBranchId] = useState<number | ''>('');
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [otpStep, setOtpStep] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpError, setOtpError] = useState('');
  const [otpInfo, setOtpInfo] = useState('');

  const { t } = useTranslation();
  const { role: currentUserRole, isAdminView } = useUserViewMode();
  const isEditMode = !!user;

  // Ekranda "0544 377 46 58" gösterilir; backend'e boşluksuz gönderilir
  const phoneDigits = phone.replace(/\s/g, '');
  const isPhoneComplete = phoneDigits.length === 11;

  // COMPANY_ADMIN, BRANCH_ADMIN ve EMPLOYEE yeni kullanıcı eklerken,
  // eklenecek kullanıcının telefonuna gönderilen SMS kodunu doğrulamak
  // zorunda (backend de aynı kuralı uygular). SUPER_ADMIN muaf.
  const requiresOtp =
    !isEditMode &&
    (currentUserRole === Role.COMPANY_ADMIN ||
      currentUserRole === Role.BRANCH_ADMIN ||
      currentUserRole === Role.EMPLOYEE);

  // Şube personeli (şube yöneticisi, çalışan) kendi şirket/şubesine ekler;
  // backend şirketi ve şubeyi token'dan sabitler.
  const isBranchStaff =
    currentUserRole === Role.BRANCH_ADMIN || currentUserRole === Role.EMPLOYEE;

  // Use the helper function to get available roles
  const availableRoles = getAvailableRoles(currentUserRole as Role);

  // Düzenleme modunda hedef kullanıcının mevcut rolü, çağıran kullanıcının
  // atayabileceği roller arasında olmayabilir (örn. bir COMPANY_ADMIN başka bir
  // COMPANY_ADMIN'i ya da kendini düzenlerken). Bu durumda:
  //  - Rolü seçeneklere ekle ki dropdown boş görünüp yanlış değer göndermesin.
  //  - Alanı kilitle ki diğer alanlar (ad/telefon/durum) düzenlenirken rol
  //    kazara değiştirilip hesap sessizce düşürülmesin.
  const roleNotReassignable =
    isEditMode && !!role && !availableRoles.includes(role as Role);
  const roleOptions = roleNotReassignable
    ? [role as Role, ...availableRoles]
    : availableRoles;

  const resetForm = () => {
    setName('');
    setSurname('');
    setEmail('');
    setPhone('');
    setRole('');
    setStatus('');
    setPassword('');
    setSelectedCompanyId('');
    setSelectedBranchId('');
    setBranches([]);
    setOtpStep(false);
    setOtpCode('');
    setOtpError('');
    setOtpInfo('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const fetchCompanies = async () => {
    try {
      setLoadingCompanies(true);
      const data = await companyService.getAll({});
      setCompanies(data.items || []);
    } catch (error) {
      console.error('Error fetching companies: ', error);
    } finally {
      setLoadingCompanies(false);
    }
  };

  const fetchBranches = async (companyId: number) => {
    try {
      setLoadingBranches(true);
      const result = await branchService.getAll({ companyId });
      setBranches(result.items || []);
    } catch (error) {
      console.error('Error fetching branches: ', error);
    } finally {
      setLoadingBranches(false);
    }
  };

  // Initialize form with user data when editing
  useEffect(() => {
    if (open) {
      if (isEditMode && user) {
        setName(user.name || '');
        setSurname(user.surname || '');
        setEmail(user.email || '');
        setPhone(formatTurkishPhone(user.phone || ''));
        setRole((user.role as Role) || '');
        setStatus((user.status as UserStatus) || '');
        setPassword(''); // Don't set password when editing

        if (user.company && user.company.id) {
          setSelectedCompanyId(user.company.id);
        }

        if (user.userBranches && user.userBranches.length > 0) {
          // Find primary branch or use first branch
          const primaryBranch = user.userBranches.find((ub) => ub.isPrimary);
          const branchToUse = primaryBranch || user.userBranches[0];
          if (branchToUse && branchToUse.branch) {
            setSelectedBranchId(branchToUse.branch.id);
          }
        } else if (user.currentBranch && user.currentBranch.id) {
          // Fallback to currentBranch for backward compatibility
          setSelectedBranchId(user.currentBranch.id);
        }

        // If company is selected, fetch branches
        if (user.company && user.company.id) {
          fetchBranches(user.company.id);
        }
      } else {
        resetForm();
      }
    }
  }, [open, user, isEditMode]);

  useEffect(() => {
    const currentUserRole = user$.role.get();
    const currentUserCompanyId = user$.company?.id?.get();
    const currentUserBranchId = user$.currentBranch?.id?.get();
    const creatorIsBranchStaff =
      currentUserRole === Role.BRANCH_ADMIN ||
      currentUserRole === Role.EMPLOYEE;

    if (!open) {
      resetForm();
      return;
    }

    if (
      role === Role.COMPANY_ADMIN ||
      (role === Role.CUSTOMER && !creatorIsBranchStaff) ||
      (role === Role.BRANCH_ADMIN && currentUserRole === Role.SUPER_ADMIN) ||
      (isBranchStaffRole(role) && currentUserRole === Role.SUPER_ADMIN)
    ) {
      fetchCompanies();
      // Only clear branch selection if not in edit mode
      if (!isEditMode) {
        setSelectedBranchId('');
      }
    } else if (
      (role === Role.BRANCH_ADMIN || isBranchStaffRole(role)) &&
      currentUserRole === Role.COMPANY_ADMIN &&
      currentUserCompanyId
    ) {
      fetchBranches(currentUserCompanyId);
      setSelectedCompanyId(currentUserCompanyId);
      if (!isEditMode && currentUserBranchId) {
        setSelectedBranchId(currentUserBranchId);
      }
    } else if (
      (isBranchStaffRole(role) || role === Role.CUSTOMER) &&
      creatorIsBranchStaff
    ) {
      // Şube yöneticisi kendi şirket/şubesine ekler; seçim gerekmez
      setSelectedBranchId(currentUserBranchId || '');
      setSelectedCompanyId(currentUserCompanyId || '');
    }
  }, [role, open, isEditMode]);

  const submitUser = async (otp?: string) => {
    await onSave({
      name,
      surname,
      email: email.trim() || undefined,
      phone: phoneDigits,
      role: role as Role,
      ...(isEditMode && status && { status: status as UserStatus }),
      ...(!isEditMode && { password }), // Only include password for new users
      ...(!isEditMode &&
        selectedCompanyId && { companyId: Number(selectedCompanyId) }),
      branchId: selectedBranchId ? Number(selectedBranchId) : undefined,
      ...(otp && { otp })
    });
  };

  const handleSendOtp = async () => {
    try {
      setLoading(true);
      setOtpError('');
      setOtpInfo('');
      const result = await authService.sendOtp({ phone: phoneDigits });
      if (result.status === 200) {
        setOtpStep(true);
        setOtpCode('');
        setOtpInfo(t('otp.sent.successfully'));
      } else {
        setOtpError(result.message || t('error.sending.otp'));
      }
    } catch (error) {
      console.error('Error sending OTP:', error);
      setOtpError(t('error.sending.otp'));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (loading) return;

    // OTP gereken rollerde önce kod gönderilir, kod girildikten sonra
    // doğrulanıp kullanıcı oluşturulur (backend kodu tekrar doğrular).
    if (requiresOtp && !otpStep) {
      await handleSendOtp();
      return;
    }

    try {
      setLoading(true);
      if (requiresOtp) {
        setOtpError('');
        const verifyResult = await authService.verifyOtp({
          phone: phoneDigits,
          otp: otpCode
        });
        if (!verifyResult.valid) {
          setOtpError(t('invalid.verification.code'));
          return;
        }
        await submitUser(otpCode);
        return;
      }
      await submitUser();
    } catch (error) {
      console.error('Error verifying OTP:', error);
      setOtpError(t('invalid.verification.code'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        className="user-add-dialog"
      >
        <DialogTitle>
          {otpStep
            ? t('phone.verification')
            : isEditMode
              ? t('edit.user')
              : t('new.user')}
          {isEditMode && ` - ${user?.name} ${user?.surname}`}
        </DialogTitle>
        {otpStep ? (
          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            {otpError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {otpError}
              </Alert>
            )}
            {otpInfo && !otpError && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {otpInfo}
              </Alert>
            )}
            <Typography sx={{ mb: 1 }}>
              {t('user.create.otp.info', { phone })}
            </Typography>
            <TextField
              required
              autoFocus
              margin="dense"
              label={t('verification.code')}
              fullWidth
              value={otpCode}
              onChange={(e) =>
                setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))
              }
              inputProps={{ inputMode: 'numeric', maxLength: 6 }}
            />
            <Button
              onClick={handleSendOtp}
              disabled={loading}
              size="small"
              sx={{ mt: 1 }}
            >
              {t('resend.code')}
            </Button>
          </DialogContent>
        ) : (
          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            {otpError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {otpError}
              </Alert>
            )}
            <TextField
              required
              autoFocus
              margin="dense"
              label={t('name')}
              type="text"
              fullWidth
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <TextField
              required
              margin="dense"
              label={t('surname')}
              type="text"
              fullWidth
              value={surname}
              onChange={(e) => setSurname(e.target.value)}
            />
            {/* Giriş telefonla; e-posta opsiyonel iletişim bilgisi. */}
            <TextField
              margin="dense"
              label={t('email')}
              type="email"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isEditMode} // Email cannot be changed when editing
            />
            <TextField
              required
              margin="dense"
              label={t('phone')}
              type="tel"
              fullWidth
              value={phone}
              onChange={(e) => setPhone(formatTurkishPhone(e.target.value))}
              placeholder="0544 377 46 58"
              inputProps={{ inputMode: 'tel', maxLength: 14 }}
            />

            {!isEditMode && (
              <TextField
                required
                margin="dense"
                label={t('password')}
                type="password"
                fullWidth
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                helperText={
                  <Typography component="span" color="textSecondary">
                    {t('create.password.for.new.user.helper')}
                  </Typography>
                }
              />
            )}

            <FormControl fullWidth margin="dense" required>
              <InputLabel>{t('role')}</InputLabel>
              <Select
                value={role}
                onChange={(e) => setRole(e.target.value as Role)}
                label={t('role')}
                disabled={roleNotReassignable}
              >
                {roleOptions.map((roleOption) => (
                  <MenuItem key={roleOption} value={roleOption}>
                    {t(`roles.${roleOption.toLowerCase()}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {isB2BRole(role) && (
              <Alert severity="info" sx={{ mt: 1 }}>
                {t('user.b2b.role.hint')}
              </Alert>
            )}

            {isEditMode && (
              <FormControl fullWidth margin="dense" required>
                <InputLabel>{t('status')}</InputLabel>
                <Select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as UserStatus)}
                  label={t('status')}
                >
                  {Object.values(UserStatus).map((statusOption) => (
                    <MenuItem key={statusOption} value={statusOption}>
                      {t(statusOption.toLowerCase())}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {(role === Role.COMPANY_ADMIN ||
              (role === Role.CUSTOMER && !isBranchStaff) ||
              ((role === Role.BRANCH_ADMIN || isBranchStaffRole(role)) &&
                currentUserRole === Role.SUPER_ADMIN)) && (
              <FormControl fullWidth margin="dense" required>
                <InputLabel>{t('company')}</InputLabel>
                <Select
                  value={
                    typeof selectedCompanyId === 'number' &&
                    companies.some((item) => item.id === selectedCompanyId)
                      ? selectedCompanyId
                      : ''
                  }
                  onChange={(e) => {
                    const companyId = Number(e.target.value);
                    setSelectedCompanyId(companyId);
                    setSelectedBranchId('');
                    if (companyId) {
                      fetchBranches(companyId);
                    } else {
                      setBranches([]);
                    }
                  }}
                  label={t('company')}
                  disabled={loadingCompanies || isEditMode}
                >
                  {companies.map((company) => (
                    <MenuItem key={company.id} value={company.id}>
                      {company.name}
                    </MenuItem>
                  ))}
                </Select>
                {loadingCompanies && (
                  <CircularProgress size={20} sx={{ ml: 1 }} />
                )}
              </FormControl>
            )}

            {(role === Role.BRANCH_ADMIN ||
              (isBranchStaffRole(role) &&
                currentUserRole !== Role.BRANCH_ADMIN)) &&
              isAdminView && (
                <FormControl fullWidth margin="dense" required>
                  <InputLabel>{t('branch')}</InputLabel>
                  <Select
                    value={
                      typeof selectedBranchId === 'number' &&
                      branches.some((item) => item.id === selectedBranchId)
                        ? selectedBranchId
                        : ''
                    }
                    onChange={(e) =>
                      setSelectedBranchId(
                        typeof e.target.value === 'number'
                          ? e.target.value
                          : Number(e.target.value)
                      )
                    }
                    label={t('branch')}
                    disabled={
                      loadingBranches ||
                      branches.length === 0 ||
                      selectedCompanyId === ''
                    }
                  >
                    {branches.map((branch) => (
                      <MenuItem key={branch.id} value={branch.id}>
                        {branch.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {loadingBranches && (
                    <CircularProgress size={20} sx={{ ml: 1 }} />
                  )}
                  {!loadingBranches && selectedCompanyId === '' && (
                    <Typography
                      color="textSecondary"
                      variant="body2"
                      sx={{ mt: 1 }}
                    >
                      {t('select.company.first')}
                    </Typography>
                  )}
                  {!loadingBranches &&
                    branches.length === 0 &&
                    selectedCompanyId !== '' && (
                      <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                        {t('no.branches.available')}
                      </Typography>
                    )}
                </FormControl>
              )}
          </DialogContent>
        )}
        <DialogActions>
          <Button
            onClick={handleClose}
            color="primary"
            disabled={loading}
            className="user-dialog-cancel-button"
          >
            {t('cancel')}
          </Button>
          {otpStep && (
            <Button
              onClick={() => {
                setOtpStep(false);
                setOtpCode('');
                setOtpError('');
                setOtpInfo('');
              }}
              color="primary"
              disabled={loading}
            >
              {t('back')}
            </Button>
          )}
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            disabled={
              otpStep
                ? otpCode.length !== 6 || loading
                : !name ||
                  !surname ||
                  !role ||
                  !isPhoneComplete ||
                  (!isEditMode && !password) ||
                  loading ||
                  (role === Role.COMPANY_ADMIN && !selectedCompanyId) ||
                  (role === Role.CUSTOMER &&
                    !isBranchStaff &&
                    !selectedCompanyId) ||
                  (role === Role.BRANCH_ADMIN &&
                    currentUserRole !== Role.SUPER_ADMIN &&
                    !selectedBranchId) ||
                  (isBranchStaffRole(role) &&
                    currentUserRole !== Role.SUPER_ADMIN &&
                    currentUserRole !== Role.BRANCH_ADMIN &&
                    (!selectedCompanyId || !selectedBranchId)) ||
                  // A B2B user without a branch link sees nothing in the B2B
                  // app (the backend links the branch only when it is sent),
                  // so a super admin must pick one too. A branch admin's own
                  // branch is pinned by the backend.
                  (isB2BRole(role) &&
                    currentUserRole !== Role.BRANCH_ADMIN &&
                    (!selectedCompanyId || !selectedBranchId))
            }
          >
            {loading ? (
              <CircularProgress size={24} />
            ) : otpStep ? (
              t('verify')
            ) : isEditMode ? (
              t('update')
            ) : requiresOtp ? (
              t('send.verification.code')
            ) : (
              t('save')
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UserDialog;
